export default function canvas() {
  const canvas = document.getElementById("canvas");
  const gl = canvas.getContext("webgl");

  if (!gl) {
    alert("WebGL がサポートされていません");
  } else if (!canvas) {
    return;
  }

  // --------- Canvas Resize ---------
  function resizeCanvas() {
    const dpr = window.devicePixelRatio || 1;
    const width = Math.floor(window.innerWidth * dpr);
    const height = Math.floor(window.innerHeight * dpr);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
  }

  // リサイズイベントリスナーの参照を保持
  const resizeHandler = resizeCanvas;
  window.addEventListener("resize", resizeHandler);
  resizeCanvas();

  // --------- Shader Source Code ---------
  const vertexShaderSource = `
  attribute vec2 a_position;
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

  const fragmentShaderSource = `
  precision mediump float;

  uniform vec2 u_resolution;
  uniform float u_time;
  uniform float u_contrast;
  uniform float u_seed;   // 実行ごとのランダムシード
  uniform float u_delay; // アニメーション開始までの遅延時間（秒）

  const int BLOB_COUNT    = 12;
  const int INITIAL_DROPS = 3; // 最初に垂らすインクの数

  float hash1(float n) {
    return fract(sin(n) * 43758.5453123);
  }

  // 少しカラフル寄りのパレット
  vec3 basePalette(float t) {
    vec3 a = vec3(0.55, 0.50, 0.45);
    vec3 b = vec3(0.45, 0.35, 0.55);
    vec3 c = vec3(1.0, 1.0, 1.0);
    vec3 d = vec3(0.15, 0.35, 0.55);
    return a + b * cos(6.28318 * (c * t + d));
  }

  // パステル化（amount=1.0 で元色、0.0 で真っ白）
  vec3 toPastel(vec3 color, float amount) {
    return mix(vec3(1.0), color, amount);
  }

  vec3 adjustContrast(vec3 color, float contrast) {
    return (color - 0.5) * contrast + 0.5;
  }

  void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution.xy;
    uv -= 0.5;
    uv.x *= u_resolution.x / u_resolution.y;

    vec3 bg = vec3(1.0);
    // 遅延時間を考慮したアニメーション時間
    float tGlobal = max(0.0, u_time - u_delay);

    float field     = 0.0;       // メタボール用フィールド
    vec3 accumColor = vec3(0.0); // 色の重み付き合計

    for (int i = 0; i < BLOB_COUNT; i++) {
      float fi = float(i);
      float tBlob = tGlobal * 0.25 + fi * 0.8;

      // デフォルト：実行ごとに変わるランダムな基準位置（中心寄り）
      float r1 = hash1(fi * 13.17 + u_seed * 1.123);
      float r2 = hash1(fi * 27.91 + u_seed * 3.57);
      vec2 baseCenter = vec2(r1 - 0.5, r2 - 0.5);

      // まずは「通常モード」の中心位置（ふわふわ動く）
      vec2 center = baseCenter + vec2(
        0.18 * sin(tBlob * 1.7 + fi * 2.1),
        0.18 * cos(tBlob * 1.3 + fi * 1.1)
      );

      // 垂らすタイミング
      float start;
      float end;

      if (i < INITIAL_DROPS) {
        // ===== 最初の 2〜3滴：画面の端から中心へ向かう =====
        // 各滴を互いに最も離れた位置に配置（120度ずつ離れた角度）
        float baseAngle = 0.0; // 基準角度（シードで少しずらす）
        float angleOffset = hash1(u_seed * 4.21) * 6.28318; // 全体を回転させる
        float angle = angleOffset + float(i) * 2.094395; // 120度 = 2.094395ラジアン
        float radius = 0.6; // 0.5 がほぼ画面端なので、少し外め

        vec2 edgePos = radius * vec2(cos(angle), sin(angle));

        // この滴が垂れ始める時間（ずらして開始）
        start = float(i) * 0.8; // 0, 0.6, 1.2 秒くらいで順にスタート
        end   = start + 3.0;

        // 中心へ向かっていく進行度（0 → 1）
        float dropDuration = 5.0; // 3秒くらいでおおよそ中心まで来る
        float tDrop = clamp((tGlobal - start) / dropDuration, 0.0, 1.0);
        float ease  = smoothstep(0.0, 1.0, tDrop); // イージング

        // edgePos から (0,0) へスライド
        vec2 towardsCenter = mix(edgePos, vec2(0.0), ease);

        // 少しだけ揺らぎを足す
        vec2 wobble = 0.08 * vec2(
          sin(tBlob * 1.9 + fi * 2.3),
          cos(tBlob * 1.4 + fi * 1.7)
        );

        center = towardsCenter + wobble;
      } else {
        // ===== それ以外のブロブ：少し遅れてからふわっと出現 =====
        float j = fi - float(INITIAL_DROPS);
        start = 1.8 + j * 0.8;
        end   = start + 3.0;
      }

      float d = length(uv - center);

      // サイズもランダム（実行ごとに変化）
      float rndSize  = hash1(fi * 41.73 + u_seed * 5.31);
      float baseSize = mix(1.0, 3.0, rndSize);

      float animAmp    = 0.5;
      float sizeVar    = sin(tBlob * 1.3 + fi);
      float sizeFactor = baseSize + sizeVar * animAmp;

      float influence = exp(-pow(d * sizeFactor, 2.0));

      // フェードイン（出現タイミングを反映）
      float localFade = smoothstep(start, end, tGlobal);
      influence *= localFade;

      if (influence < 0.001) {
        continue;
      }

      // インクの色：3色（ブルー、マゼンタ、グリーン）から選ぶ
      vec3 blobColor;

      // 3色の定義
      vec3 colorBlue   = vec3(0.25, 0.45, 0.75);  // ブルー系
      vec3 colorMagenta = vec3(0.75, 0.35, 0.75);  // マゼンタ（ピンク）系
      vec3 colorGreen  = vec3(0.35, 0.65, 0.45); // グリーン系

      if (i < 3) {
        // 最初の3滴：必ずブルー、マゼンタ、グリーンを1つずつランダムな順番で割り当て
        // シードベースで順列を生成（0,1,2の順番をシャッフル）
        float r0 = hash1(u_seed * 7.13);
        float r1 = hash1(u_seed * 7.13 + 1.0);

        // 最初の色のインデックス（0,1,2から選ぶ）
        int idx0 = int(r0 * 3.0);

        // 2番目の色のインデックス（残り2つから選ぶ）
        int idx1;
        if (idx0 == 0) {
          idx1 = int(r1 * 2.0) + 1; // 1 or 2
        } else if (idx0 == 1) {
          idx1 = int(r1 * 2.0) == 0 ? 0 : 2; // 0 or 2
        } else {
          idx1 = int(r1 * 2.0); // 0 or 1
        }

        // 3番目の色のインデックス（残り1つ）
        int idx2 = 3 - idx0 - idx1;

        // 現在の滴（i）の色を決定
        int colorIdx;
        if (i == 0) {
          colorIdx = idx0;
        } else if (i == 1) {
          colorIdx = idx1;
        } else {
          colorIdx = idx2;
        }

        if (colorIdx == 0) {
          blobColor = colorBlue;
        } else if (colorIdx == 1) {
          blobColor = colorMagenta;
        } else {
          blobColor = colorGreen;
        }
      } else {
        // それ以降：3色からランダムに選ぶ（シードベースなので実行ごとに固定）
        float colorChoice = hash1(fi * 0.73 + u_seed * 0.91);

        if (colorChoice < 0.333) {
          blobColor = colorBlue;
        } else if (colorChoice < 0.666) {
          blobColor = colorMagenta;
        } else {
          blobColor = colorGreen;
        }
      }

      // パステル弱めで色味はっきり
      blobColor = toPastel(blobColor, 0.8);

      field      += influence;
      accumColor += blobColor * influence;
    }

    vec3 col = bg;

    if (field > 0.0) {
    vec3 inkColor = accumColor / field;

    // ① ガンマ補正で中間調を少し明るくする（γ < 1.0）
    //    0.8〜0.9 くらいが扱いやすい
    inkColor = pow(inkColor, vec3(0.95));

    // ② インクが「たくさん重なっているところ」ほど少し明るくブースト
    float threshold = 0.40;
    float edge      = 0.08;

    // field が threshold を超えると 0→1 で効いてくる
    float brightBoost = 0.25 * smoothstep(threshold, threshold * 3.0, field);
    inkColor = clamp(inkColor + brightBoost, 0.0, 1.0);

    // メタボールの輪郭
    float mask = smoothstep(threshold - edge, threshold + edge, field);

  col = mix(bg, inkColor, mask);
}

    float globalFade = smoothstep(0.0, 3.0, tGlobal);
    vec3 finalColor = mix(bg, col, globalFade);

    finalColor = adjustContrast(finalColor, u_contrast * 1.1);

    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

  // --------- Shader Compile Utility ---------
  function createShader(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  function createProgram(gl, vsSource, fsSource) {
    const vs = createShader(gl, gl.VERTEX_SHADER, vsSource);
    const fs = createShader(gl, gl.FRAGMENT_SHADER, fsSource);
    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      return null;
    }
    return program;
  }

  // --------- Program Setup ---------
  const program = createProgram(gl, vertexShaderSource, fragmentShaderSource);
  gl.useProgram(program);

  // Fullscreen Quad
  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  const positions = new Float32Array([
    -1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1,
  ]);
  gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

  const aPositionLocation = gl.getAttribLocation(program, "a_position");
  gl.enableVertexAttribArray(aPositionLocation);
  gl.vertexAttribPointer(aPositionLocation, 2, gl.FLOAT, false, 0, 0);

  // uniforms
  const uResolutionLocation = gl.getUniformLocation(program, "u_resolution");
  const uTimeLocation = gl.getUniformLocation(program, "u_time");
  const uContrastLocation = gl.getUniformLocation(program, "u_contrast");
  const uSeedLocation = gl.getUniformLocation(program, "u_seed");
  const uDelayLocation = gl.getUniformLocation(program, "u_delay");

  // コントラスト初期値
  let contrast = 1.0;

  // ★ 実行毎に違う値になるランダムシード（0〜1000くらい）
  const seed = Math.random() * 1000;

  // ★ アニメーション開始までの遅延時間（秒） - JavaScript側から変更可能
  let animationDelay = 2.0;
  animationDelay = 0.0;

  // --------- Render Loop ---------
  let startTime = performance.now();
  let animationFrameId = null;
  let isDestroyed = false;

  function render() {
    if (isDestroyed) {
      return; // 破棄済みの場合は何もしない
    }

    resizeCanvas();
    gl.viewport(0, 0, canvas.width, canvas.height);

    const time = (performance.now() - startTime) * 0.001;

    gl.clearColor(1.0, 1.0, 1.0, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.uniform2f(uResolutionLocation, canvas.width, canvas.height);
    gl.uniform1f(uTimeLocation, time);
    gl.uniform1f(uContrastLocation, contrast);
    gl.uniform1f(uSeedLocation, seed);
    gl.uniform1f(uDelayLocation, animationDelay); // 遅延時間を渡す

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    animationFrameId = requestAnimationFrame(render);
  }

  // アニメーションを開始
  animationFrameId = requestAnimationFrame(render);

  // --------- Destroy Function ---------
  function destroy() {
    if (isDestroyed) {
      return; // 既に破棄済み
    }

    // アニメーションループを停止
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    // イベントリスナーを削除
    window.removeEventListener("resize", resizeHandler);

    // 破棄フラグを設定
    isDestroyed = true;

    // WebGLリソースのクリーンアップ（オプション）
    // 必要に応じて、バッファやシェーダープログラムも削除できます
    // gl.deleteBuffer(positionBuffer);
    // gl.deleteProgram(program);
  }

  return { destroy };
}
